import { Link, createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Car, Loader2, Send } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAccounts } from '@/lib/use-accounts'
import { useBookCar, useVehicles } from '@/lib/api'
import { WebhookManagement } from '@/components/webhook-management'
import { LoopIntervalSettings } from '@/components/loop-interval-settings'
import { localizeDigits } from '@/lib/localize-digits'
import { getApiErrorMessage } from '@/lib/utils'

export const Route = createFileRoute('/settings/advanced')({
  component: AdvancedFeaturesPage,
})

function AdvancedFeaturesPage() {
  const { t } = useTranslation()
  const { accounts: accountsList, isLoading: accountsLoading } = useAccounts()
  const [carNumber, setCarNumber] = useState('')
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Fetch vehicles to look up car by number
  const { data: vehiclesData, refetch: refetchVehicles } = useVehicles({
    enabled: false,
  })

  // Use the booking mutation
  const bookCarMutation = useBookCar()
  const isSubmitting = bookCarMutation.isPending

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage(null)

    if (!carNumber.trim()) {
      setErrorMessage(t('advanced.quickBook.pleaseEnterCarNumber'))
      return
    }

    if (!selectedAccountId) {
      setErrorMessage(t('advanced.quickBook.pleaseSelectAccount'))
      return
    }

    // Parse the car number from input
    const carNumberParsed = parseInt(carNumber.trim(), 10)
    if (isNaN(carNumberParsed)) {
      setErrorMessage(t('advanced.quickBook.invalidCarNumber'))
      return
    }

    try {
      // Fetch latest vehicles to find the car by number
      const { data: freshVehicles } = await refetchVehicles()

      if (!freshVehicles?.vehicles || freshVehicles.vehicles.length === 0) {
        setErrorMessage(t('advanced.quickBook.noVehiclesAvailable'))
        return
      }

      // Find the vehicle with matching vehicleNb
      const vehicle = freshVehicles.vehicles.find(
        (v) => v.vehicleNb === carNumberParsed,
      )

      if (!vehicle) {
        setErrorMessage(
          t('advanced.quickBook.carNotFound', { number: carNumberParsed }),
        )
        return
      }

      // Book the car using its vehicleId
      await bookCarMutation.mutateAsync({
        accountName: selectedAccountId,
        vehicleId: vehicle.vehicleId,
      })

      const account = accountsList.find((acc) => acc.id === selectedAccountId)

      toast.success(
        `${t('advanced.quickBook.bookingSuccess')} - ${account?.name} #${localizeDigits(carNumber)}${vehicle.model ? ` (${vehicle.model})` : ''}`,
      )

      // Reset form
      setCarNumber('')
      setSelectedAccountId('')
    } catch (error) {
      console.error('Error booking car:', error)
      setErrorMessage(
        getApiErrorMessage(error, t('advanced.quickBook.failedToBook')),
      )
    }
  }

  return (
    <div>
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-3">
          <div className="flex items-center gap-3">
            <Link to="/settings">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold">{t('advanced.title')}</h1>
              <p className="text-xs text-muted-foreground">
                {t('advanced.description')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        <div className="space-y-4 pt-4">
          {/* Quick Book Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="h-5 w-5" />
                {t('advanced.quickBook.title')}
              </CardTitle>
              <CardDescription>
                {t('advanced.quickBook.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Error Message */}
                {errorMessage && (
                  <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                    {errorMessage}
                  </div>
                )}

                {/* Account Selection */}
                <div className="space-y-2">
                  <Label htmlFor="account">
                    {t('advanced.quickBook.selectAccount')}
                  </Label>
                  <Select
                    value={selectedAccountId}
                    onValueChange={(value) => {
                      setSelectedAccountId(value)
                      setErrorMessage(null)
                    }}
                  >
                    <SelectTrigger id="account" className="w-full">
                      <SelectValue
                        placeholder={t('advanced.quickBook.chooseAccount')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {accountsLoading && (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          {t('common.loading')}...
                        </div>
                      )}
                      {!accountsLoading && accountsList.length === 0 && (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          {t('advanced.quickBook.noAccounts')}
                        </div>
                      )}
                      {accountsList.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{account.name}</span>
                            {account.email && (
                              <span className="text-xs text-muted-foreground">
                                {account.email}
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Car Number Input */}
                <div className="space-y-2">
                  <Label htmlFor="carNumber">
                    {t('advanced.quickBook.carNumber')}
                  </Label>
                  <Input
                    id="carNumber"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder={t('advanced.quickBook.carNumberPlaceholder')}
                    value={carNumber}
                    onChange={(e) => {
                      setCarNumber(e.target.value)
                      setErrorMessage(null)
                    }}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('advanced.quickBook.carNumberDescription')}
                  </p>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={
                    isSubmitting || !carNumber.trim() || !selectedAccountId
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 me-2 animate-spin" />
                      {t('advanced.quickBook.searching')}
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 me-2" />
                      {t('advanced.quickBook.bookCar')}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Loop Interval Settings */}
          <LoopIntervalSettings />

          {/* Webhook Management */}
          <WebhookManagement />
        </div>
      </div>
    </div>
  )
}
